// ============================================================================
// Termes typés du noyau (lot 2 de la refonte, docs/moteur/REFONTE_MOTEUR.md).
//
//   - `Term` : arbre EXACT (nombres rationnels sur BigInt, jamais de flottant).
//     La soustraction est une somme avec un terme multiplié par −1 ; le pourcentage
//     une division par 100 ; « x² » une puissance d'exposant 2.
//   - `fromNode` : arbre du moteur actuel (tree.ts) → Term. Le lexique et le
//     parseur restent PARTAGÉS : une ligne de l'élève se lit une seule fois.
//   - `canon` : forme canonique LÉGÈRE (aplatissement, nombres calculés,
//     coefficient en tête, opérandes triés SEULEMENT si l'opération est
//     commutative pour leur sorte). Elle ne développe ni ne factorise rien :
//     c'est la base de la reconnaissance des règles.
//   - `termText` : écriture relue à l'identique par notre parseur.
// ============================================================================

import { parseText, type Eq, type Line, type Node } from '../tree';
import * as Q from './exact';
import type { Rat } from './exact';
import type { Sort } from './types';

export const FN_NAMES = [
  'sqrt',
  'abs',
  'fact',
  'deg',
  'ln',
  'exp',
  'cos',
  'sin',
  'tan',
  'arg',
  'arccos',
  'arcsin',
  'arctan',
  // moteur v45 : logarithme décimal
  'log',
] as const;
export type FnName = (typeof FN_NAMES)[number];

export type Term =
  | { k: 'num'; v: Rat }
  | { k: 'sym'; n: string }
  | { k: 'add'; c: Term[] }
  | { k: 'mul'; c: Term[] }
  | { k: 'div'; a: Term; b: Term }
  | { k: 'pow'; a: Term; b: Term }
  | { k: 'fn'; f: FnName; a: Term }
  /** « a parmi b » (moteur v27) : coefficient binomial, a choisis parmi b */
  | { k: 'binom'; a: Term; b: Term };

export type Rel = '=' | '<' | '>' | '≤' | '≥';
export interface Relation {
  rel: Rel;
  l: Term;
  r: Term;
}
/**
 * Une ligne : une expression, une relation, des égalités reliées par « ou », ou un
 * ENSEMBLE (moteur v23) : des clauses reliées par « ou », chacune une ou plusieurs
 * relations vraies ensemble (une chaîne « −1 < x ≤ 2 » est deux relations). Toute
 * disjonction qui contient une comparaison est un ensemble : « x ≤ 2 ou x ≥ 3 ».
 */
export type Statement =
  | { k: 'expr'; e: Term }
  | ({ k: 'rel' } & Relation)
  | { k: 'or'; items: Relation[] }
  | { k: 'set'; clauses: Relation[][] };

/** Toutes les relations d'un énoncé (clauses d'un ensemble aplaties). */
export function relationsIn(s: Statement): Relation[] {
  if (s.k === 'rel') return [{ rel: s.rel, l: s.l, r: s.r }];
  if (s.k === 'or') return s.items;
  if (s.k === 'set') return s.clauses.flat();
  return [];
}

/** Même énoncé, chaque membre transformé. */
export function mapStatement(s: Statement, f: (t: Term) => Term): Statement {
  const map = (i: Relation): Relation => ({ rel: i.rel, l: f(i.l), r: f(i.r) });
  if (s.k === 'expr') return { k: 'expr', e: f(s.e) };
  if (s.k === 'rel') return { k: 'rel', ...map(s) };
  if (s.k === 'set') return { k: 'set', clauses: s.clauses.map((c) => c.map(map)) };
  return { k: 'or', items: s.items.map(map) };
}

/** Sortes des symboles (absent : réel). */
export type Sorts = Readonly<Record<string, Sort>>;

/* ------------------------------ constructeurs ------------------------------ */

export const num = (v: Rat | number): Term => ({
  k: 'num',
  v: typeof v === 'number' ? Q.rat(v) : v,
});
export const sym = (n: string): Term => ({ k: 'sym', n });
export const add = (...c: Term[]): Term => ({ k: 'add', c });
export const mul = (...c: Term[]): Term => ({ k: 'mul', c });
export const div = (a: Term, b: Term): Term => ({ k: 'div', a, b });
export const pow = (a: Term, b: Term): Term => ({ k: 'pow', a, b });
export const fn = (f: FnName, a: Term): Term => ({ k: 'fn', f, a });
/** « k parmi n » : `k` choisis parmi `n` (moteur v27) */
export const binom = (k: Term, n: Term): Term => ({ k: 'binom', a: k, b: n });
export const neg = (a: Term): Term => (a.k === 'num' ? num(Q.neg(a.v)) : mul(num(-1), a));
export const sub = (a: Term, b: Term): Term => add(a, neg(b));

export const isNum = (t: Term): t is { k: 'num'; v: Rat } => t.k === 'num';
export const isPatternVar = (t: Term): boolean => t.k === 'sym' && t.n.startsWith('?');
/** π : constante, jamais une inconnue */
export const CONSTANTS: Readonly<Record<string, 1>> = { π: 1 };

/* --------------------------- arbre actuel → Term --------------------------- */

export class UnsupportedTerm extends Error {
  constructor(public readonly what: string) {
    super(`non pris en charge : ${what}`);
  }
}

/**
 * L'écriture décimale d'un nombre JS, sans exposant : « 3.5e-7 » → « 0.00000035 »
 * (un décimal saisi avec 7 décimales ou plus, moteur v38 ; « 1e+30 » aussi).
 */
function plainDecimal(v: number): string {
  const s = String(v);
  const m = /^(-?)(\d+)(?:\.(\d+))?e([+-]\d+)$/.exec(s);
  if (!m) return s;
  const digits = m[2]! + (m[3] ?? '');
  const point = m[2]!.length + Number(m[4]);
  const body =
    point <= 0
      ? '0.' + '0'.repeat(-point) + digits
      : point >= digits.length
        ? digits + '0'.repeat(point - digits.length)
        : digits.slice(0, point) + '.' + digits.slice(point);
  return m[1] + body;
}

/** Nombre JS (lu d'un décimal saisi) → rationnel exact. */
function exactFromFloat(v: number): Rat {
  if (!Number.isFinite(v)) throw new UnsupportedTerm('nombre infini');
  const r = Q.parseDecimal(plainDecimal(v));
  if (!r) throw new UnsupportedTerm(`nombre ${v}`);
  return r;
}

/** Arbre du moteur actuel → Term exact. Lève `UnsupportedTerm` pour ce que le noyau ne lit pas. */
export function fromNode(n: Node): Term {
  switch (n.k) {
    case 'n':
      return num(exactFromFloat(n.v!));
    case 's':
      return sym(n.n!);
    case 'add':
      return add(...n.c!.map(fromNode));
    case 'mul':
      return mul(...n.c!.map(fromNode));
    case 'div':
      return div(fromNode(n.a!), fromNode(n.b!));
    case 'pow':
      return pow(fromNode(n.a!), fromNode(n.b!));
    case 'neg':
      return neg(fromNode(n.a!));
    case 'sq':
      return pow(fromNode(n.a!), num(2));
    case 'pct':
      return div(fromNode(n.a!), num(100));
    case 'binom':
      return binom(fromNode(n.a!), fromNode(n.b!));
    case 'sqrt':
    case 'abs':
    case 'fact':
    case 'deg':
    case 'ln':
    case 'exp':
    case 'cos':
    case 'sin':
    case 'tan':
    case 'arg':
    case 'arccos':
    case 'arcsin':
    case 'arctan':
    case 'log':
      return fn(n.k, fromNode(n.a!));
  }
  throw new UnsupportedTerm(n.k);
}

/** Une chaîne « a < x ≤ b » : ses relations consécutives (moteur v23). */
function chainOf(eq: Eq): Relation[] | null {
  if (!eq.chain || eq.chain.length !== eq.sides.length - 1) return null;
  const sides = eq.sides.map(fromNode);
  return eq.chain.map((rel, i) => ({ rel, l: sides[i]!, r: sides[i + 1]! }));
}

function relationOf(eq: Eq): Relation | Term | null {
  if (eq.chain) return null;
  if (eq.rels || eq.mod !== undefined) return null;
  if (eq.cmp) {
    if (eq.cmp === '≈') return null;
    return { rel: eq.cmp, l: fromNode(eq.sides[0]!), r: fromNode(eq.sides[1]!) };
  }
  if (eq.sides.length === 1) return fromNode(eq.sides[0]!);
  if (eq.sides.length === 2)
    return { rel: '=', l: fromNode(eq.sides[0]!), r: fromNode(eq.sides[1]!) };
  return null;
}

export type StatementResult =
  | { ok: true; s: Statement }
  | { ok: false; reason: 'unparsable' | 'unsupported' };

/** Texte d'une ligne → énoncé. Le lexique et le parseur sont ceux du moteur actuel. */
export function parseStatement(text: string): StatementResult {
  let line;
  try {
    line = parseText(text);
  } catch {
    return { ok: false, reason: 'unparsable' };
  }
  return statementOfLine(line);
}

/**
 * Ligne déjà lue par le moteur actuel (`Line` de tree.ts) → énoncé (lot 3 : adaptateur
 * `legacyLine → Term`). Une chaîne « a = b = c », une congruence ou « ≈ » : `unsupported`
 * (l'adaptateur de comparaison découpe les chaînes lui-même).
 */
export function statementOfLine(line: Line): StatementResult {
  try {
    // une chaîne, ou une disjonction qui contient une comparaison : un ensemble (v23)
    const isSet =
      line.dis.some((d) => d.chain) ||
      (line.dis.length > 1 && line.dis.some((d) => d.cmp && d.cmp !== '≈'));
    if (isSet) {
      const clauses: Relation[][] = [];
      for (const d of line.dis) {
        const chain = chainOf(d);
        if (chain) {
          clauses.push(chain);
          continue;
        }
        const r = relationOf(d);
        if (!r || !('rel' in r)) return { ok: false, reason: 'unsupported' };
        clauses.push([r]);
      }
      return { ok: true, s: { k: 'set', clauses } };
    }
    if (line.dis.length > 1) {
      const items: Relation[] = [];
      for (const d of line.dis) {
        const r = relationOf(d);
        if (!r || !('rel' in r) || r.rel !== '=') return { ok: false, reason: 'unsupported' };
        items.push(r);
      }
      return { ok: true, s: { k: 'or', items } };
    }
    const r = relationOf(line.dis[0]!);
    if (r === null) return { ok: false, reason: 'unsupported' };
    return { ok: true, s: 'rel' in r ? { k: 'rel', ...r } : { k: 'expr', e: r } };
  } catch (e) {
    if (e instanceof UnsupportedTerm) return { ok: false, reason: 'unsupported' };
    throw e;
  }
}

/* ------------------------------ sortes ------------------------------ */

/** Le terme fait-il intervenir une matrice ? (la multiplication n'y est pas commutative) */
export function involvesMatrix(t: Term, sorts: Sorts): boolean {
  switch (t.k) {
    case 'num':
      return false;
    case 'sym':
      return sorts[t.n] === 'matrix';
    case 'add':
    case 'mul':
      return t.c.some((c) => involvesMatrix(c, sorts));
    case 'div':
    case 'pow':
      return involvesMatrix(t.a, sorts) || involvesMatrix(t.b, sorts);
    case 'fn':
    case 'binom':
      return false;
  }
}

/* ------------------------------ clés ------------------------------ */

export function termKey(t: Term): string {
  switch (t.k) {
    case 'num':
      return Q.ratToText(t.v);
    case 'sym':
      return `$${t.n}`;
    case 'add':
      return `+(${t.c.map(termKey).join(',')})`;
    case 'mul':
      return `*(${t.c.map(termKey).join(',')})`;
    case 'div':
      return `/(${termKey(t.a)},${termKey(t.b)})`;
    case 'pow':
      return `^(${termKey(t.a)},${termKey(t.b)})`;
    case 'fn':
      return `${t.f}(${termKey(t.a)})`;
    case 'binom':
      return `C(${termKey(t.a)},${termKey(t.b)})`;
  }
}

export function statementKey(s: Statement): string {
  if (s.k === 'expr') return `E:${termKey(s.e)}`;
  if (s.k === 'rel') return `R:${s.rel}:${termKey(s.l)}|${termKey(s.r)}`;
  if (s.k === 'set')
    return `S:${s.clauses
      .map((c) => c.map((i) => `${i.rel}:${termKey(i.l)}|${termKey(i.r)}`).join('&'))
      .join(';')}`;
  return `O:${s.items.map((i) => `${termKey(i.l)}|${termKey(i.r)}`).join(';')}`;
}

/* ------------------------------ canonisation ------------------------------ */

const MAX_EXACT_POW = 64n;

function exactSqrt(q: Rat): Rat | null {
  const r = Q.sqrtRat(q);
  return r && Q.radIsRational(r) ? r.coef : null;
}

/**
 * Forme canonique légère. Ce qu'elle fait : aplatir sommes et produits, calculer
 * les sous-termes purement numériques, regrouper les nombres d'un produit en un
 * coefficient placé en tête, retirer les « + 0 » et « × 1 », trier les opérandes
 * d'une opération commutative POUR LEUR SORTE (jamais le produit de matrices).
 * Ce qu'elle ne fait pas : développer, factoriser, réduire des termes semblables.
 */
export function canon(t: Term, sorts: Sorts = {}): Term {
  switch (t.k) {
    case 'num':
    case 'sym':
      return t;
    case 'add': {
      const flat: Term[] = [];
      let k = Q.ZERO;
      for (const c of t.c.map((x) => canon(x, sorts))) {
        for (const d of c.k === 'add' ? c.c : [c]) {
          if (d.k === 'num') k = Q.add(k, d.v);
          else flat.push(d);
        }
      }
      // constante en dernier : « x² + 9 », jamais « 9 + x² »
      const terms = flat.sort(byKey);
      if (!Q.isZero(k)) terms.push(num(k));
      if (terms.length === 0) return num(0);
      if (terms.length === 1) return terms[0]!;
      return { k: 'add', c: terms };
    }
    case 'mul': {
      const flat: Term[] = [];
      let k = Q.ONE;
      for (const c of t.c.map((x) => canon(x, sorts))) {
        for (const d of c.k === 'mul' ? c.c : [c]) {
          if (d.k === 'num') k = Q.mul(k, d.v);
          else flat.push(d);
        }
      }
      if (Q.isZero(k)) return num(0);
      const commutative = !flat.some((f) => involvesMatrix(f, sorts));
      const rest = commutative ? mergePowers(flat, sorts).sort(byKey) : flat;
      if (rest.length === 0) return num(k);
      if (Q.eq(k, Q.ONE)) return rest.length === 1 ? rest[0]! : { k: 'mul', c: rest };
      return { k: 'mul', c: [num(k), ...rest] };
    }
    case 'div': {
      const a = canon(t.a, sorts);
      const b = canon(t.b, sorts);
      if (b.k === 'num' && Q.eq(b.v, Q.ONE)) return a;
      if (a.k === 'num' && b.k === 'num') {
        const q = Q.div(a.v, b.v);
        if (q) return num(q);
      }
      return { k: 'div', a, b };
    }
    case 'pow': {
      const a = canon(t.a, sorts);
      const b = canon(t.b, sorts);
      if (b.k === 'num' && Q.eq(b.v, Q.ONE)) return a;
      if (a.k === 'num' && b.k === 'num' && Q.isInteger(b.v) && Q.abs(b.v).n <= MAX_EXACT_POW) {
        const p = Q.pow(a.v, Number(b.v.n));
        if (p) return num(p);
      }
      return { k: 'pow', a, b };
    }
    case 'fn': {
      const a = canon(t.a, sorts);
      if (a.k === 'num') {
        if (t.f === 'sqrt') {
          const r = exactSqrt(a.v);
          if (r) return num(r);
        }
        if (t.f === 'abs') return num(Q.abs(a.v));
        if (t.f === 'fact' && Q.isInteger(a.v) && a.v.n >= 0n && a.v.n <= 20n) {
          let f = 1n;
          for (let i = 2n; i <= a.v.n; i++) f *= i;
          return num(Q.rat(f));
        }
      }
      return { k: 'fn', f: t.f, a };
    }
    case 'binom': {
      // « 5 parmi 49 » se calcule (moteur v27) ; « k parmi n » reste écrit
      const k = canon(t.a, sorts);
      const n = canon(t.b, sorts);
      if (k.k === 'num' && n.k === 'num') {
        const v = binomialExact(n.v, k.v);
        if (v !== null) return num(v);
      }
      return { k: 'binom', a: k, b: n };
    }
  }
}

/** Plus grand n pour lequel « k parmi n » et n! se calculent exactement. */
export const MAX_EXACT_FACT = 1000n;

/**
 * « k parmi n » exact (moteur v27) pour des entiers naturels n ≤ 1000 : 0 si k > n.
 * null : hors domaine (négatif, non entier) ou trop grand.
 */
export function binomialExact(n: Rat, k: Rat): Rat | null {
  if (!Q.isInteger(n) || !Q.isInteger(k) || n.n < 0n || k.n < 0n || n.n > MAX_EXACT_FACT)
    return null;
  if (k.n > n.n) return Q.ZERO;
  const kk = k.n < n.n - k.n ? k.n : n.n - k.n;
  let r = 1n;
  for (let i = 1n; i <= kk; i++) r = (r * (n.n - kk + i)) / i;
  return Q.rat(r);
}

/** Produit commutatif : facteurs de même base réunis (x × x → x², x × x² → x³). */
function mergePowers(fs: Term[], sorts: Sorts): Term[] {
  const groups = new Map<string, { base: Term; e: Q.Rat; n: number }>();
  const order: string[] = [];
  for (const f of fs) {
    const intPow = f.k === 'pow' && f.b.k === 'num' && Q.isInteger(f.b.v);
    const base = intPow ? (f as { a: Term }).a : f;
    const e = intPow ? (f as { b: { v: Q.Rat } }).b.v : Q.ONE;
    const key = termKey(base);
    const g = groups.get(key);
    if (g) {
      g.e = Q.add(g.e, e);
      g.n++;
    } else {
      groups.set(key, { base, e, n: 1 });
      order.push(key);
    }
  }
  const out: Term[] = [];
  for (const key of order) {
    const g = groups.get(key)!;
    if (g.n === 1) {
      out.push(Q.eq(g.e, Q.ONE) ? g.base : { k: 'pow', a: g.base, b: num(g.e) });
      continue;
    }
    if (Q.isZero(g.e)) continue;
    out.push(canon(Q.eq(g.e, Q.ONE) ? g.base : { k: 'pow', a: g.base, b: num(g.e) }, sorts));
  }
  return out;
}

function byKey(a: Term, b: Term): number {
  const ka = termKey(a);
  const kb = termKey(b);
  return ka < kb ? -1 : ka > kb ? 1 : 0;
}

/** Égalité à la forme canonique légère près. */
export function sameTerm(a: Term, b: Term, sorts: Sorts = {}): boolean {
  return termKey(canon(a, sorts)) === termKey(canon(b, sorts));
}

/** Symboles (hors constantes et variables de motif) d'un terme. */
export function symbolsOf(t: Term, acc: Set<string> = new Set()): Set<string> {
  switch (t.k) {
    case 'sym':
      if (!CONSTANTS[t.n]) acc.add(t.n);
      break;
    case 'add':
    case 'mul':
      t.c.forEach((c) => symbolsOf(c, acc));
      break;
    case 'div':
    case 'pow':
    case 'binom':
      symbolsOf(t.a, acc);
      symbolsOf(t.b, acc);
      break;
    case 'fn':
      symbolsOf(t.a, acc);
      break;
    default:
      break;
  }
  return acc;
}

export function statementSymbols(s: Statement): Set<string> {
  const acc = new Set<string>();
  if (s.k === 'expr') symbolsOf(s.e, acc);
  else relationsIn(s).forEach((i) => (symbolsOf(i.l, acc), symbolsOf(i.r, acc)));
  return acc;
}

/** Remplace des symboles par des termes (substitution simultanée). */
export function substitute(t: Term, map: Readonly<Record<string, Term>>): Term {
  switch (t.k) {
    case 'num':
      return t;
    case 'sym':
      return map[t.n] ?? t;
    case 'add':
      return add(...t.c.map((c) => substitute(c, map)));
    case 'mul':
      return mul(...t.c.map((c) => substitute(c, map)));
    case 'div':
      return div(substitute(t.a, map), substitute(t.b, map));
    case 'pow':
      return pow(substitute(t.a, map), substitute(t.b, map));
    case 'fn':
      return fn(t.f, substitute(t.a, map));
    case 'binom':
      return binom(substitute(t.a, map), substitute(t.b, map));
  }
}

/* ------------------------------ écriture ------------------------------ */

// « k parmi n » (moteur v27) lie plus fort que × et ÷, moins fort que le signe
const PREC = { add: 1, mul: 2, div: 2, binom: 3, unary: 4, pow: 5, atom: 6 } as const;

/** Écriture décimale exacte (« 1,414 ») si le dénominateur ne contient que 2 et 5, au plus 6 décimales. */
function decimalText(q: Rat): string | null {
  let d = q.d;
  let twos = 0;
  let fives = 0;
  while (d % 2n === 0n) {
    d /= 2n;
    twos++;
  }
  while (d % 5n === 0n) {
    d /= 5n;
    fives++;
  }
  const places = Math.max(twos, fives);
  if (d !== 1n || places === 0 || places > 6) return null;
  const n = q.n < 0n ? -q.n : q.n;
  const digits = ((n * 10n ** BigInt(places)) / q.d).toString().padStart(places + 1, '0');
  return `${q.n < 0n ? '−' : ''}${digits.slice(0, -places)},${digits.slice(-places)}`;
}

/** Nombre lisible : entier, fraction simple « 3 ÷ 4 », décimal exact « 0,15 » si le dénominateur dépasse 12. */
function ratText(q: Rat): { text: string; prec: number } {
  const s = q.n < 0n ? '−' : '';
  if (q.d === 1n)
    return { text: s + (q.n < 0n ? -q.n : q.n).toString(), prec: s ? PREC.unary : PREC.atom };
  const dec = q.d > 12n ? decimalText(q) : null;
  if (dec) return { text: dec, prec: s ? PREC.unary : PREC.atom };
  return { text: `${s}${q.n < 0n ? -q.n : q.n} ÷ ${q.d}`, prec: PREC.mul };
}

function wrap(x: { text: string; prec: number }, min: number): string {
  return x.prec < min ? `(${x.text})` : x.text;
}

/** coefficient numérique (en tête d'un produit canonique) */
function splitCoef(t: Term): { coef: Rat; rest: Term[] } {
  if (t.k === 'num') return { coef: t.v, rest: [] };
  if (t.k === 'mul') {
    const first = t.c[0];
    return first?.k === 'num' ? { coef: first.v, rest: t.c.slice(1) } : { coef: Q.ONE, rest: t.c };
  }
  return { coef: Q.ONE, rest: [t] };
}

/** juxtaposition sans « × » après un coefficient entier : 12x, 3(x + 1), 2√x, 5x² */
function implicitAfterNumber(t: Term): boolean {
  if (t.k === 'sym') return true;
  if (t.k === 'pow') return t.a.k === 'sym';
  if (t.k === 'fn') return t.f === 'sqrt';
  return t.k === 'add';
}

function factorText(f: Term): string {
  const s = show(f);
  // (2 parmi 4) × (3 parmi 28) : lisible (moteur v27)
  return s.text.startsWith('−') || s.prec < PREC.mul || f.k === 'div' || f.k === 'binom'
    ? `(${s.text})`
    : s.text;
}

function mulText(coef: Rat, rest: Term[]): { text: string; prec: number } {
  if (rest.length === 0) return ratText(coef);
  const negative = Q.sign(coef) < 0;
  const c = Q.abs(coef);
  // « kπ », « 2kπ » (moteur v24) : π se colle à la lettre qui le précède
  const factors = rest
    .map((f) => ({ f, t: factorText(f) }))
    .reduce<string[]>((acc, { f, t }, i) => {
      const before = rest[i - 1];
      if (i > 0 && f.k === 'sym' && f.n === 'π' && before?.k === 'sym' && before.n !== 'π')
        acc[acc.length - 1] += t;
      else acc.push(t);
      return acc;
    }, []);
  let body: string;
  if (Q.eq(c, Q.ONE)) body = factors.join(' × ');
  else if (c.d === 1n) {
    const n = c.n.toString();
    body = implicitAfterNumber(rest[0]!)
      ? n + factors[0] + (factors.length > 1 ? ' × ' + factors.slice(1).join(' × ') : '')
      : `${n} × ${factors.join(' × ')}`;
  } else if (c.n !== 1n && decimalText(c) && implicitAfterNumber(rest[0]!)) {
    // 1,5x
    body =
      decimalText(c)! +
      factors[0] +
      (factors.length > 1 ? ' × ' + factors.slice(1).join(' × ') : '');
  } else {
    // x ÷ 2, 3x ÷ 7
    body = `${mulText(Q.rat(c.n), rest).text} ÷ ${c.d}`;
  }
  return { text: (negative ? '−' : '') + body, prec: negative ? PREC.unary : PREC.mul };
}

function show(t: Term): { text: string; prec: number } {
  switch (t.k) {
    case 'num':
      return ratText(t.v);
    case 'sym':
      return { text: t.n, prec: PREC.atom };
    case 'add': {
      // affichage : commencer par un terme positif quand il y en a un (« 4x − 3x », pas « −3x + 4x »)
      const shown = t.c.map(show);
      const firstPos = shown.findIndex((x) => !x.text.startsWith('−'));
      const order =
        firstPos > 0
          ? [firstPos, ...shown.keys()].filter((i, j) => j === 0 || i !== firstPos)
          : [...shown.keys()];
      let text = '';
      order.forEach((idx, i) => {
        const s = shown[idx]!;
        if (i === 0) text = s.text;
        else if (s.prec < PREC.mul) text += ` + (${s.text})`;
        else if (s.text.startsWith('−')) text += ` − ${s.text.slice(1)}`;
        else text += ` + ${s.text}`;
      });
      return { text, prec: PREC.add };
    }
    case 'mul': {
      const { coef, rest } = splitCoef(t);
      return mulText(coef, rest);
    }
    case 'div':
      return {
        text: `${t.a.k === 'binom' ? `(${show(t.a).text})` : wrap(show(t.a), PREC.mul)} ÷ ${wrap(show(t.b), PREC.pow)}`,
        prec: PREC.mul,
      };
    case 'binom':
      return {
        text: `${wrap(show(t.a), PREC.pow)} parmi ${wrap(show(t.b), PREC.pow)}`,
        prec: PREC.binom,
      };
    case 'pow': {
      const base = wrap(show(t.a), PREC.atom);
      if (t.b.k === 'num' && Q.eq(t.b.v, Q.rat(2))) return { text: `${base}²`, prec: PREC.pow };
      return { text: `${base}^${wrap(show(t.b), PREC.atom)}`, prec: PREC.pow };
    }
    case 'fn': {
      const a = show(t.a);
      switch (t.f) {
        case 'sqrt':
          return { text: `√${wrap(a, PREC.atom)}`, prec: PREC.unary };
        case 'abs':
          return { text: `|${a.text}|`, prec: PREC.atom };
        case 'fact':
          return { text: `${wrap(a, PREC.atom)}!`, prec: PREC.pow };
        case 'deg':
          return { text: `${wrap(a, PREC.atom)}°`, prec: PREC.pow };
        default:
          return { text: `${t.f}(${a.text})`, prec: PREC.unary };
      }
    }
  }
}

/** Écriture mathématique relue par notre parseur (« 12x + 5 », « x = 3 ÷ 4 »). */
export function termText(t: Term): string {
  return show(t).text;
}

export function statementText(s: Statement): string {
  if (s.k === 'expr') return termText(s.e);
  if (s.k === 'rel') return `${termText(s.l)} ${s.rel} ${termText(s.r)}`;
  if (s.k === 'set') return s.clauses.map(clauseText).join(' ou ');
  return s.items.map((i) => `${termText(i.l)} ${i.rel} ${termText(i.r)}`).join(' ou ');
}
/** Une clause d'un ensemble : une relation, ou une chaîne « −1 < x ≤ 2 ». */
function clauseText(c: Relation[]): string {
  let out = termText(c[0]!.l);
  c.forEach((i, k) => {
    // une chaîne partage ses membres : « a < x » puis « x ≤ b » (une clause vient toujours
    // d'une chaîne ; « et » ne se relit pas, il ne sert qu'à ne pas mentir)
    if (k > 0 && termKey(c[k - 1]!.r) !== termKey(i.l)) out += ` et ${termText(i.l)}`;
    out += ` ${i.rel} ${termText(i.r)}`;
  });
  return out;
}
